import React, { Component } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import lodashCompact from 'lodash/fp/compact';

import {
  LISTING_SERVICE_TYPE_PRE_LEASE,
  LISTING_SERVICE_TYPE_FULL_SERVICE,
  LISTING_STATUS_ACTIVE,
  LISTING_STATUS_PENDING,
} from 'common/listingConstants';

import {
  dataTable,
  actionDataTableLoadNextPage,
} from 'client/helpers/dataTables';
import withInfiniteScroll from 'client/helpers/withInfiniteScroll';

import {
  getListingFieldsForDisplay,
  getListingManagedPhotoUrls,
  isPreLeaseActive,
} from 'common/listingHelpers';

import {
  abbreviateNumber,
  numberWithCommas,
  bedsDisplayString,
  bathsDisplayString,
} from 'common/formatters';

import {
  DATA_TABLE_SOURCE_PARTNER_LISTINGS,
  DATA_TABLE_VIEW_ID_PARTNER_LISTINGS_ALL,
  DATA_TABLE_VIEW_ID_PARTNER_LISTINGS_APP_IN_PROGRESS,
} from 'common/dataTableConstants';

import branch from 'client/core/branch';

import SrPartnerListingCard, {
  SR_PARTNER_LISTING_CARD_TAG_THEME_GREEN,
  SR_PARTNER_LISTING_CARD_TAG_THEME_PURPLE,
  SR_PARTNER_LISTING_CARD_TAG_THEME_WHITE_BLACK,
  SR_PARTNER_LISTING_CARD_TAG_THEME_WHITE_ORANGE,
  SR_PARTNER_LISTING_CARD_TAG_THEME_YELLOW,
  SR_PARTNER_LISTING_CARD_TAG_THEME_BLUE,
} from 'client/components/SrPartnerListingCard';
import SrPartnerList from 'client/components/SrPartnerList';
import SrIcon from 'client/components/SrIcon';

import {
  MARKETING_PARTNER_LISTING_AGREEMENT_SIGNED_BY_SUNROOM,
} from 'common/marketingPartnerListingConstants';

export function makeStatLabel(title, total) {
  return `${abbreviateNumber(total, 1)} ${pluralize(title, total)}`;
}

export function getDataTableSettingsFromProps(props) {
  const rootViewId = props.partnerListingsViewId || DATA_TABLE_VIEW_ID_PARTNER_LISTINGS_ALL;
  const viewId = `${rootViewId}_${props.partner.id}`;
  const marketIds = props.currentUser?.settings?.partnerPortal?.activeListings?.[props.partner.slug + '_marketIds']; // eslint-disable-line max-len
  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_PARTNER_LISTINGS,
    viewId,
    filter: {
      'listings.status': [
        LISTING_STATUS_ACTIVE,
        LISTING_STATUS_PENDING,
      ],
      'listings.partnerId': props.partner.id,
    },
    customParams: {
      partnerListings: {
        sinceDate: props.sinceDate,
        services: [
          LISTING_SERVICE_TYPE_FULL_SERVICE,
          LISTING_SERVICE_TYPE_PRE_LEASE,
        ],
      },
    },
    sort: {
      lastActiveDate: 'desc',
    },
    count: 40,
  };

  if (props.sinceDate) {
    dataTableSettings.viewId = `${dataTableSettings.viewId}_sinceDate:${props.sinceDate}`;
  }

  if (typeof props.apartmentBuildingId !== 'undefined') {
    dataTableSettings.filter['listings.apartmentBuildingId'] = props.apartmentBuildingId;
    const viewIdSuffix = Array.isArray(props.apartmentBuildingId)
      ? props.apartmentBuildingId.join('-')
      : props.apartmentBuildingId;

    dataTableSettings.viewId = `${dataTableSettings.viewId}_apartmentBuildingId:${viewIdSuffix}`;
  }

  if (rootViewId === DATA_TABLE_VIEW_ID_PARTNER_LISTINGS_APP_IN_PROGRESS) {
    dataTableSettings.customParams.partnerListings.appInProgress = true;
  }
  if (marketIds && marketIds.length > 0) {
    dataTableSettings.customParams.partnerListings.marketIds = marketIds;
    dataTableSettings.viewId = `${viewId}_marketIds:${marketIds.join('_')}`; // eslint-disable-line max-len
  }

  return dataTableSettings;
}

@branch({
  currentUser: ['currentUser'],
})
@dataTable(getDataTableSettingsFromProps)
@withInfiniteScroll({
  offset: 80,
})
class SrDataTablePartnerListings extends Component {
  static propTypes = {
    partner: PropTypes.object.isRequired, // eslint-disable-line react/no-unused-prop-types
    items: PropTypes.array.isRequired,
    apartment: PropTypes.object,
    apartmentBuildingId: PropTypes.array, // eslint-disable-line react/no-unused-prop-types
    dispatch: PropTypes.func.isRequired,
    dataTableApiState: PropTypes.object.isRequired,
    dataTableIsFullyLoaded: PropTypes.bool.isRequired,
    onInfiniteScrollWrapperRef: PropTypes.func.isRequired,
    onInfiniteScrollCallbackReady: PropTypes.func.isRequired,
    partnerListingsViewId: PropTypes.string, // eslint-disable-line react/no-unused-prop-types
  }

  constructor(props) {
    super(props);

    const { onInfiniteScrollCallbackReady } = props;
    onInfiniteScrollCallbackReady(this._infiniteScrollCallback);
  }

  _infiniteScrollCallback = () => {
    const { dataTableIsFullyLoaded, dispatch } = this.props;
    if (!dataTableIsFullyLoaded) {
      dispatch(actionDataTableLoadNextPage, getDataTableSettingsFromProps(this.props));
    }
  }

  _getTags = (listing) => {
    return lodashCompact([
      listing.isShowUnit && {
        theme: SR_PARTNER_LISTING_CARD_TAG_THEME_WHITE_ORANGE,
        label: 'show unit',
      },
      listing.appApproved && {
        theme: SR_PARTNER_LISTING_CARD_TAG_THEME_GREEN,
        label: 'lease pending',
      },
      listing.activeConcessions.length > 0 && {
        theme: SR_PARTNER_LISTING_CARD_TAG_THEME_BLUE,
        label: 'rent concession',
      },
      listing.appInProgress && {
        theme: SR_PARTNER_LISTING_CARD_TAG_THEME_PURPLE,
        label: 'apps in progress',
      },
      listing.listingAgreementSignedBy === MARKETING_PARTNER_LISTING_AGREEMENT_SIGNED_BY_SUNROOM
      && {
        theme: SR_PARTNER_LISTING_CARD_TAG_THEME_YELLOW,
        label: 'client listing',
      },
      isPreLeaseActive({ listing }) && {
        theme: SR_PARTNER_LISTING_CARD_TAG_THEME_BLUE,
        label: 'pre-lease',
      },
    ]);
  }

  _getStats = (listing) => {
    const listingFieldsForDisplay = getListingFieldsForDisplay({
      listing: listing,
    });
    return [
      `$${numberWithCommas(listingFieldsForDisplay.price)}`,
      bedsDisplayString(listingFieldsForDisplay),
      bathsDisplayString(listingFieldsForDisplay),
      `${numberWithCommas(listingFieldsForDisplay.squareFeet)} ft`,
    ];
  }

  _getDaysOnMarket = (listing) => {
    const daysOnMarketText = listing.daysOnMarket
      ? pluralize('day', listing.daysOnMarket, true)
      : null;
    return daysOnMarketText;
  }

  _getTitle = (listing) => {
    const { apartment } = this.props;
    const isUnit = !!apartment;
    let title = listing.addressStreetDisplay;

    if (isUnit) {
      const apartmentUnit = listing.apartmentUnit || '';
      title = apartmentUnit.toLowerCase().includes('unit')
        ? apartmentUnit
        : (
          <span>
            <span>{`Unit ${apartmentUnit || '--'} `}</span>
            {listing.floorPlanName && (
              <span className='floorPlanName'>
                {`(${listing.floorPlanName})`}
              </span>
            )}
          </span>
        );
    }
    return title;
  }

  _getCaption = (listing) => {
    const { apartment } = this.props;
    const isUnit = !!apartment;

    if (isUnit) {
      return [
        `${listing.apartmentName} |`,
        listing.addressStreetNumber,
        listing.addressStreetName,
      ].join(' ');
    }

    return '';
  }

  _getBottomChips = (listing) => {
    const label = [
      `${listing.addressCity},`,
      listing.addressStateCode,
    ].join(' ');

    return lodashCompact([
      {
        label,
        theme: SR_PARTNER_LISTING_CARD_TAG_THEME_WHITE_BLACK,
      },
      listing.propertyManager && {
        label: (
          <>
            <SrIcon className='person-single-dark' marginRight={8} display='inlineBlock' />
            {listing.propertyManager.name}
          </>
        ),
        theme: SR_PARTNER_LISTING_CARD_TAG_THEME_WHITE_BLACK,
      },
    ]);
  }

  render() {
    const {
      items: listings,
      onInfiniteScrollWrapperRef,
      dataTableApiState,
      partner,
    } = this.props;

    return (
      <div ref={onInfiniteScrollWrapperRef}>
        <SrPartnerList singleRow>
          {listings.map((listing) => {
            const [listingPhoto] = getListingManagedPhotoUrls({
              listing,
              useWatermarks: false,
            });
            const daysOnMarket = this._getDaysOnMarket(listing);
            const stats = this._getStats(listing);
            const tags = this._getTags(listing);
            const title = this._getTitle(listing);
            const caption = this._getCaption(listing);
            const bottomChips = this._getBottomChips(listing);

            return (
              <SrPartnerListingCard
                key={listing.id}
                to={`/partner/${partner.slug}/listing/${listing.id}`}
                photos={[listingPhoto]}
                title={title}
                caption={caption}
                date={daysOnMarket}
                stats={stats}
                tags={tags}
                bottomChips={bottomChips}
              />
            );
          })}
        </SrPartnerList>

        {dataTableApiState.inProgress && (
          <div className='loading-ellipses' />
        )}
      </div>
    );
  }
}

SrDataTablePartnerListings.displayName = 'SrDataTablePartnerListings';

export default SrDataTablePartnerListings;
